import * as React from 'react';
import { withIntl, IntlProps, Language } from 'ha/i18n';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';

import Container from 'ha/ui/next/Container';
import Grid from 'ha/ui/next/Grid';
import units from 'ha/theme/units';
import formatHrefURL from 'ha/utils/formatHrefURL';
import { Typography } from 'ha/ui/next/Typography';
import Track from 'ha/components/track/SmartTrack';
import Link from 'ha/ui/next/Link';
import hasCommunity from 'ha/modules/Hermes/utils/hasCommunity';
import hasLandlordsCommunity from 'ha/modules/Hermes/utils/hasLandlordsCommunity';
import isContentfulSupported from 'ha/modules/Hermes/utils/isContentfulSupported';

import Facebook from 'streamline-icons/share-facebook.svg';
import Linkedin from 'streamline-icons/share-linkedin.svg';
import Twitter from 'streamline-icons/share-twitter.svg';
import Youtube from 'streamline-icons/share-youtube.svg';

import IconButton from 'ha/ui/next/IconButton';
import SvgIcon from 'ha/ui/next/SvgIcon';

export const NOFOLLOW_REF = 'nofollow noopener noreferrer';

const styles = (theme: Theme) =>
  createStyles({
    footer: {
      backgroundColor: theme.palette.darkBlue.main,
      paddingTop: units.rem(3),
      paddingBottom: units.rem(1),
    },
    column: {
      textAlign: 'center',
      [theme.breakpoints.up('md')]: {
        textAlign: 'left',
      },
    },
    columnTitle: {
      textAlign: 'center',
      marginTop: units.rem(2),
      marginBottom: units.rem(0.2),

      [theme.breakpoints.up('md')]: {
        marginTop: units.rem(0),
        textAlign: 'left',
      },
    },
    socialRow: {
      marginTop: units.rem(1),
      flexDirection: 'column-reverse',

      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
    },
    followUsTitle: {
      marginRight: units.rem(0.5),
    },
    icon: {
      backgroundColor: 'white',
      color: 'black',
      marginRight: units.rem(0.5),
      '&:hover': {
        backgroundColor: 'white',
      },
    },
    svg: {
      width: units.rem(1),
      height: units.rem(1),
    },
    link: {
      display: 'inline-block',
      margin: units.rem(0.5),

      [theme.breakpoints.up('md')]: {
        display: 'block',
        margin: 0,
      },
    },
    socialButtons: {
      justifyContent: 'center',
      marginBottom: units.rem(1),

      [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-end',
        marginBottom: units.rem(0),
      },
    },
  });

interface AvailableLanguagesProps {
  localisedText: string;
  code: Language;
}

interface Props {
  availableLanguages: AvailableLanguagesProps[];
  noFollow?: boolean;
}

export const Footer: React.SFC<
  Props & IntlProps & WithStyles<typeof styles>
> = ({
  classes,
  availableLanguages,
  noFollow = true,
  intl: { T, urlResolver, lang },
}) => {
  const rel = noFollow ? NOFOLLOW_REF : '';

  return (
    <footer className={classes.footer}>
      <Container maxWidth="lg">
        <Grid container>
          <Grid className={classes.column} item xs={12} md={2}>
            {availableLanguages.map(({ code, localisedText }) => (
              <Link to={formatHrefURL('/', code)} key={code} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T(localisedText)}
                </Typography.Paragraph>
              </Link>
            ))}
          </Grid>
          <Grid className={classes.column} item xs={12} md={2}>
            <Typography.ParagraphTitle
              className={classes.columnTitle}
              color="white"
            >
              HousingAnywhere
            </Typography.ParagraphTitle>

            <Track name="Clicked Careers in footer" type="icon">
              <Link
                target="_blank"
                to={urlResolver.getCareersUrl()}
                rel={NOFOLLOW_REF}
              >
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('Careers')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked Press releases in footer" type="link">
              <Link to={urlResolver.getPressUrl()} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('Press')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked University plans in footer" type="link">
              <Link to={urlResolver.getUniversityPlansUrl()} rel={NOFOLLOW_REF}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('University plans')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked Terms and conditions in footer" type="link">
              <Link to={urlResolver.getTermsUrl()} rel={NOFOLLOW_REF}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('terms_header')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked Data Security in footer" type="link">
              <Link to={urlResolver.getDataSecurityUrl()} rel={NOFOLLOW_REF}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('data_security_header')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked Cookie Policy in footer" type="link">
              <Link to={urlResolver.getCookiePolicyUrl()} rel={NOFOLLOW_REF}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('cookie_policy_header')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked Sitemap in footer" type="link">
              <Link rel={NOFOLLOW_REF} to={urlResolver.getSitemapUrl()}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('Sitemap')}
                </Typography.Paragraph>
              </Link>
            </Track>
          </Grid>
          <Grid className={classes.column} item xs={12} md={2}>
            <Typography.ParagraphTitle
              className={classes.columnTitle}
              color="white"
            >
              {T('Tenants')}
            </Typography.ParagraphTitle>

            <Track name="Clicked How to rent in footer" type="link">
              <Link to={urlResolver.getRenting()} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('How to rent')}
                </Typography.Paragraph>
              </Link>
            </Track>

            {hasCommunity(lang) && (
              <Track name="Clicked Blog in footer" type="link">
                <Link to={urlResolver.getCommunityUrl(lang)}>
                  <Typography.Paragraph color="white" className={classes.link}>
                    {T('Blog for internationals')}
                  </Typography.Paragraph>
                </Link>
              </Track>
            )}
          </Grid>
          <Grid className={classes.column} item xs={12} md={2}>
            <Typography.ParagraphTitle
              className={classes.columnTitle}
              color="white"
            >
              {T('Landlords')}
            </Typography.ParagraphTitle>
            {hasLandlordsCommunity(lang) && (
              <Track name="Clicked Landlord community in footer" type="link">
                <Link to={urlResolver.getLandlordsCommunityUrl(lang)} rel={rel}>
                  <Typography.Paragraph color="white" className={classes.link}>
                    {T('Blog for landlords')}
                  </Typography.Paragraph>
                </Link>
              </Track>
            )}

            <Track name="Clicked How to rent out in footer" type="link">
              <Link to={urlResolver.getRentingOut()} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('How to rent out')}
                </Typography.Paragraph>
              </Link>
            </Track>

            {isContentfulSupported(lang) && (
              <Track name="Clicked Become an advertiser in footer" type="link">
                <Link to={urlResolver.getBecomeAnAdvertiserUrl()}>
                  <Typography.Paragraph color="white" className={classes.link}>
                    {T('Become an advertiser')}
                  </Typography.Paragraph>
                </Link>
              </Track>
            )}

            <Track name="Clicked HousingAnywhere Api in footer" type="link">
              <Link
                to={urlResolver.getDevelopersUrl()}
                target="_blank"
                rel={rel}
              >
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('HousingAnywhere API')}
                </Typography.Paragraph>
              </Link>
            </Track>

            {isContentfulSupported(lang) && (
              <Link to={urlResolver.getHelpCenterUrl(lang)} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('Help Center')}
                </Typography.Paragraph>
              </Link>
            )}

            <Track
              name="Clicked Legal rental information in footer"
              type="link"
            >
              <Link to={urlResolver.getLegalUrl()} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('legal_short')}
                </Typography.Paragraph>
              </Link>
            </Track>
          </Grid>
          <Grid className={classes.column} item xs={12} md={2}>
            <Typography.ParagraphTitle
              className={classes.columnTitle}
              color="white"
            >
              {T('Support')}
            </Typography.ParagraphTitle>

            <Track name="Clicked FAQ in footer" type="link">
              <Link to={urlResolver.getAnswersUrl()} rel={rel}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('faq_short')}
                </Typography.Paragraph>
              </Link>
            </Track>

            <Track name="Clicked Contact us in footer" type="link">
              <Link to={urlResolver.getContactUrl()}>
                <Typography.Paragraph color="white" className={classes.link}>
                  {T('contact_us_header')}
                </Typography.Paragraph>
              </Link>
            </Track>
          </Grid>
        </Grid>
        <Grid
          container
          justify="space-between"
          alignItems="center"
          className={classes.socialRow}
        >
          <Grid item xs={12} md={8}>
            <Typography.Paragraph color="white" className={classes.link}>
              {T('Housing Anywhere © 2009 - 2017')}
            </Typography.Paragraph>
          </Grid>
          <Grid
            item
            container
            alignItems="center"
            xs={12}
            md={4}
            className={classes.socialButtons}
          >
            <Typography.Paragraph
              color="white"
              className={classes.followUsTitle}
            >
              {T('Follow us')}
            </Typography.Paragraph>
            <div>
              <Track
                name="Clicked Social icon"
                type="icon"
                socialvendor="facebook"
              >
                <IconButton
                  href={urlResolver.getFacebookUrl()}
                  target="_blank"
                  rel={NOFOLLOW_REF}
                  className={classes.icon}
                >
                  <SvgIcon className={classes.svg} children={<Facebook />} />
                </IconButton>
              </Track>
              <Track name="Clicked Social icon" type="icon">
                <IconButton
                  href={urlResolver.getTwitterUrl()}
                  target="_blank"
                  rel={NOFOLLOW_REF}
                  className={classes.icon}
                >
                  <SvgIcon className={classes.svg} children={<Twitter />} />
                </IconButton>
              </Track>
              <Track
                name="Clicked Social icon"
                type="icon"
                socialvendor="linkedin"
              >
                <IconButton
                  href={urlResolver.getLinkedinUrl()}
                  rel={NOFOLLOW_REF}
                  className={classes.icon}
                >
                  <SvgIcon className={classes.svg} children={<Linkedin />} />
                </IconButton>
              </Track>
              <Track
                name="Clicked Social icon"
                type="icon"
                socialvendor="youtube"
              >
                <IconButton
                  href={urlResolver.getYoutubeUrl()}
                  target="_blank"
                  rel={NOFOLLOW_REF}
                  className={classes.icon}
                >
                  <SvgIcon className={classes.svg} children={<Youtube />} />
                </IconButton>
              </Track>
            </div>
          </Grid>
        </Grid>
      </Container>
    </footer>
  );
};

export default withIntl(withStyles(styles)(Footer));
